import { useState, useCallback, useEffect, useRef } from 'react';

export interface ResizablePanelOptions {
  /** Minimum width in pixels. */
  minWidth: number;
  /** Maximum width in pixels. */
  maxWidth: number;
  /** Initial width in pixels. */
  defaultWidth: number;
  /**
   * Resize direction:
   * - `'right'` — handle is on the right edge, dragging right increases width (left-anchored panels).
   * - `'left'`  — handle is on the left edge, dragging left increases width (right-anchored panels).
   */
  direction?: 'left' | 'right';
}

export interface ResizablePanelResult {
  /** Current panel width. */
  width: number;
  /** Whether the user is currently dragging. */
  isDragging: boolean;
  /** Attach this to the resize handle's `onMouseDown`. */
  onResizeStart: (e: React.MouseEvent) => void;
}

/**
 * Hook for making a panel resizable via drag handle.
 *
 * Manages mousedown/mousemove/mouseup lifecycle, cursor feedback,
 * and user-select prevention during drag.
 */
export function useResizablePanel({
  minWidth,
  maxWidth,
  defaultWidth,
  direction = 'right',
}: ResizablePanelOptions): ResizablePanelResult {
  const [width, setWidth] = useState(defaultWidth);
  const [isDragging, setIsDragging] = useState(false);
  const isDraggingRef = useRef(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(defaultWidth);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDraggingRef.current) return;
      const delta = e.clientX - startXRef.current;
      const adjusted = direction === 'right' ? delta : -delta;
      const newWidth = Math.min(maxWidth, Math.max(minWidth, startWidthRef.current + adjusted));
      setWidth(newWidth);
    },
    [minWidth, maxWidth, direction],
  );

  const handleMouseUp = useCallback(() => {
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;
    setIsDragging(false);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  const onResizeStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      isDraggingRef.current = true;
      setIsDragging(true);
      startXRef.current = e.clientX;
      startWidthRef.current = width;
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    [width],
  );

  return { width, isDragging, onResizeStart };
}
